import React, { useState, useEffect } from 'react';
import { Box, Avatar } from '@mui/material';
import { deleteIssuer, getAllIssuers, updateIssuer } from '../../../services/apiservice';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import IssuerEditModal from './IssuerEditModal';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

function IssuerList() {
  const { data: fetchedIssuers, loading } = useApiData(() => getAllIssuers().then((res) => res.data), []);
  const [issuers, setIssuers] = useState([]);
  const [loadingIssuers, setLoadingIssuers] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIssuer, setSelectedIssuer] = useState(null);

  useEffect(() => {
    if (fetchedIssuers) {
      setIssuers(fetchedIssuers);
    }
  }, [fetchedIssuers]);

  const handleEdit = (issuer) => {
    setSelectedIssuer(issuer);
    setModalOpen(true);
  };

  const handleDelete = async (issuer) => {
    const IssuerID = issuer.IssuerID;
    try {
      setLoadingIssuers(prevState => ({ ...prevState, [IssuerID]: true }));
      const response = await deleteIssuer(IssuerID);
      if (response.success) {
        setIssuers(issuers.filter(i => i.IssuerID !== IssuerID));
        setSnackbarMessage('Issuer deleted successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.message || 'Failed to delete Issuer.');
        setSnackbarSeverity('error');
      }
    }
    catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setLoadingIssuers(prevState => ({ ...prevState, [IssuerID]: false }));
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedIssuer(null);
  };

  const handleSave = async (updatedIssuer) => {
    try {
      const response = await updateIssuer(updatedIssuer);
      if (response.success) {
        setIssuers((prevIssuers) =>
          prevIssuers.map((issuer) =>
            issuer.IssuerID === updatedIssuer.IssuerID ? { ...issuer, ...updatedIssuer } : issuer
          )
        );
        setSnackbarMessage('Issuer updated successfully!');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'IssuerID', label: 'Issuer ID', width: 100 },
    { key: 'IssuerName', label: 'Issuer Name', width: 200 },
    {
      key: 'repName',
      label: 'Representative Full Name',
      width: 200,
      render: (issuer) => (
        <Box display="flex" alignItems="center">
          <Avatar src={issuer.IssuerLogoURL} sx={{ width: 40, height: 40, marginRight: 2 }} />
          <span>{`${issuer.IssuerRepFirstName || ''} ${issuer.IssuerRepLastName || ''}`}</span>
        </Box>
      ),
    },
    { key: 'IssuerRepEmailID', label: 'Representative Email ID', width: 200 },
    { key: 'IssuerRepPhoneNumber', label: 'Representative Phone Number', width: 150 },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Issuers List"
        columns={columns}
        rows={issuers}
        loading={loading}
        searchKeys={['IssuerName', (i) => `${i.IssuerRepFirstName || ''} ${i.IssuerRepLastName || ''}`, 'IssuerRepEmailID', 'IssuerRepPhoneNumber', 'IssuerWebsite']}
        rowKey="IssuerID"
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleting={loadingIssuers}
      />
      <IssuerEditModal
        open={modalOpen}
        onClose={handleModalClose}
        issuer={selectedIssuer}
        onSave={handleSave}
      />
    </>
  );
}

export default IssuerList;
